import PropTypes from 'prop-types';
import React from 'react';

import { Color, colorList, FontSize, fontSizeList, FontWeight, fontWeightList } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { placeholdersMap, PLAYERAVATAR } from '../../../utils/placeholders-football';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import { IconPosition, iconPositions } from '../../molecules/team/team';
import ImgPlaceholder from '../img-placeholder/img-placeholder';
import Text, { TextTags } from '../text/text';

import './avatar.scss';

export interface IAvatarLabel {
  colorLabel?: Color;
  fontSizeLabel?: FontSize;
  fontWeightLabel?: FontWeight;
  tagLabel?: TextTags;
}

export interface IAvatarName {
  colorName?: Color;
  fontSizeName?: FontSize;
  fontWeightName?: FontWeight;
  tagName?: TextTags;
}
export interface IAvatar extends IAvatarLabel, IAvatarName, IComponent {
  borderColor?: Color;
  borderThickness?: Number;
  imagePosition?: IconPosition;
  imageUrl: string;
  isCircled?: boolean;
  label?: string;
  name?: string;
  url?: string;
  placeholder?: string;
}

const classNames = (
  isCircled: boolean,
  borderColor: Color,
  imagePosition: IconPosition,
  borderThickness?: Number
): IClassNames =>
  makeClassNames({
    avatar: ['avatar', imagePosition && `avatar--image-${imagePosition}`],
    avatarImage: [
      'avatar--image',
      isCircled && `avatar--image-circled`,
      borderColor && `avatar--image-border-${borderColor}`,
      (borderThickness || borderThickness === 0) && `avatar--image-border-w${borderThickness}`,
    ],
    avatarInfo: 'avatar--info',
    avatarLabel: 'avatar--info-label',
    avatarName: 'avatar--info-name',
  });

const Avatar: React.FC<IAvatar> = ({
  /* istanbul ignore next: default value */ borderColor = 'red-light',
  /* istanbul ignore next: default value */ colorLabel = 'blue-light',
  /* istanbul ignore next: default value */ colorName = 'blue',
  /* istanbul ignore next: default value */ fontSizeLabel = '12',
  /* istanbul ignore next: default value */ fontSizeName = '12',
  /* istanbul ignore next: default value */ fontWeightLabel = '400',
  /* istanbul ignore next: default value */ fontWeightName = '500',
  /* istanbul ignore next: default value */ imagePosition = 'left',
  /* istanbul ignore next: default value */ isCircled = false,
  /* istanbul ignore next: default value */ placeholder = placeholdersMap[PLAYERAVATAR],
  /* istanbul ignore next: default value */ tagLabel = 'span',
  /* istanbul ignore next: default value */ tagName = 'p',
  /* istanbul ignore next: default value */ trackingData = {},
  borderThickness,
  imageUrl,
  label,
  name,
  url,
}) => {
  const cn = classNames(isCircled, borderColor, imagePosition, borderThickness);

  const avatarImage = (
    <div className={cn.avatarImage}>
      <ImgPlaceholder role="presentation" loading="lazy" title={name} src={imageUrl} placeholder={placeholder} />
    </div>
  );

  const avatarInfo = (
    <div className={cn.avatarInfo}>
      {label && (
        <Text
          className={cn.avatarLabel}
          tag={tagLabel}
          fontSize={fontSizeLabel}
          fontWeight={fontWeightLabel}
          color={colorLabel}
        >
          {label}
        </Text>
      )}
      {name && (
        <Text
          className={cn.avatarName}
          tag={tagName}
          fontSize={fontSizeName}
          fontWeight={fontWeightName}
          color={colorName}
        >
          {name}
        </Text>
      )}
    </div>
  );

  return url ? (
    <a className={cn.avatar} href={url} {...objectToDataAttributes(trackingData)}>
      {avatarImage}
      {avatarInfo}
    </a>
  ) : (
    <div className={cn.avatar}>
      {avatarImage}
      {avatarInfo}
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  Avatar.propTypes = {
    borderColor: PropTypes.oneOf(colorList),
    borderThickness: PropTypes.number,
    colorLabel: PropTypes.oneOf(colorList),
    colorName: PropTypes.oneOf(colorList),
    fontSizeLabel: PropTypes.oneOf(fontSizeList),
    fontSizeName: PropTypes.oneOf(fontSizeList),
    fontWeightLabel: PropTypes.oneOf(fontWeightList),
    fontWeightName: PropTypes.oneOf(fontWeightList),
    imagePosition: PropTypes.oneOf(iconPositions),
    imageUrl: PropTypes.string.isRequired,
    isCircled: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    trackingData: PropTypes.object,
    url: PropTypes.string,
  };
}

export default Avatar;
